import type { Product } from '../../../data/product';
import { defineColumn, type StrictColumnDef } from '../../tanstack-table/column';

/**
 * The product columns that are nothing but a field read: an id, widths, and an
 * accessor, with no custom cell, sort or CSV handling of their own (ORD-270).
 * They lived in one file each, which made `columns/` harder to scan than the
 * columns that actually carry logic.
 *
 * Each still calls `defineColumn` directly rather than going through a narrower
 * wrapper — `defineColumn` already is the factory, and a positional helper over
 * it could express less than it does (`gloss` and `cell` below would not fit).
 *
 * The `id` strings are load-bearing: they are the column keys, `defineColumn`
 * builds its sort cache off them, and the spaces in 'Std Qty' / 'Order Lot' are
 * part of the key. `case-qty-column.tsx` stays where it is — it renders a
 * custom cell that is paired with its own test.
 */

export const Barcode: StrictColumnDef<Product> = defineColumn<Product>({
  id: 'Barcode',
  minWidth: 100,
  maxWidth: 128,
  accessorFn: (product) => product.barcode,
});

export const Brand: StrictColumnDef<Product> = defineColumn<Product>({
  id: 'Brand',
  minWidth: 150,
  maxWidth: 280,
  accessorFn: (product) => product.brand,
});

export const Department: StrictColumnDef<Product> = defineColumn<Product>({
  id: 'Department',
  minWidth: 100,
  maxWidth: 136,
  accessorFn: (product) => product.department,
});

export const Description: StrictColumnDef<Product> = defineColumn<Product>({
  id: 'Description',
  minWidth: 200,
  accessorFn: (product) => product.description,
});

export const OrderLot: StrictColumnDef<Product> = defineColumn<Product>({
  id: 'Order Lot',
  minWidth: 45,
  accessorFn: (product) => product.order_lot,
  gloss:
    'Whether you order by the case (C) or by the unit (U). When set to C, Orderbot blocks unit-quantity orders.',
});

export const StdQty: StrictColumnDef<Product> = defineColumn<Product>({
  id: 'Std Qty',
  minWidth: 40,
  accessorFn: (product) => `${product.standard_qty}`,
  gloss: 'How many units make up one pack — for example, a single can vs. a 6-pack of beer.',
});

export const Type: StrictColumnDef<Product> = defineColumn<Product>({
  id: 'Type',
  minWidth: 120,
  maxWidth: 256,
  accessorFn: (product) => product.type,
});
